import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Database, Lock, Trash2, Mail, Calendar } from 'lucide-react';

const LAST_UPDATED = '2026년 1월 15일';

export function PrivacyPage() {
  const sections = [
    {
      icon: Database,
      title: '수집하는 개인정보',
      content: [
        {
          subtitle: '필수 정보',
          items: [
            '이메일 주소: 계정 생성 및 로그인에 사용됩니다.',
            '비밀번호: 계정 보안을 위해 암호화되어 저장됩니다.',
          ],
        },
        {
          subtitle: '서비스 이용 정보',
          items: [
            '북마크 URL 및 메타데이터: 저장하신 웹페이지의 URL, 제목, 설명, 파비콘 정보',
            '컬렉션 및 태그: 북마크 정리를 위해 생성하신 컬렉션과 태그 정보',
            '서비스 이용 기록: 북마크 생성일, 수정일 등의 시간 정보',
          ],
        },
      ],
    },
    {
      icon: Shield,
      title: '개인정보의 이용 목적',
      content: [
        {
          items: [
            '서비스 제공: 북마크 저장, 관리, 동기화 기능 제공',
            '계정 관리: 회원 가입, 로그인, 비밀번호 복구',
            '서비스 개선: 사용 패턴 분석을 통한 서비스 품질 향상',
            '고객 지원: 문의 응대 및 공지사항 전달',
          ],
        },
      ],
    },
    {
      icon: Lock,
      title: '개인정보의 보호',
      content: [
        {
          items: [
            '모든 데이터는 SSL/TLS 암호화를 통해 전송됩니다.',
            '비밀번호는 단방향 해시 함수로 암호화되어 저장됩니다.',
            'Supabase의 보안 인프라를 활용하여 데이터를 안전하게 보관합니다.',
            '정기적인 보안 점검을 통해 취약점을 모니터링합니다.',
          ],
        },
      ],
    },
    {
      icon: Calendar,
      title: '개인정보의 보유 및 파기',
      content: [
        {
          subtitle: '보유 기간',
          items: [
            '회원 탈퇴 시까지 개인정보를 보유합니다.',
            '관련 법령에 따라 일정 기간 보관이 필요한 경우 해당 기간 동안 보관합니다.',
          ],
        },
        {
          subtitle: '파기 절차',
          items: [
            '회원 탈퇴 요청 시 지체 없이 개인정보를 파기합니다.',
            '전자적 파일 형태의 정보는 복구할 수 없는 방법으로 영구 삭제합니다.',
          ],
        },
      ],
    },
    {
      icon: Trash2,
      title: '이용자의 권리',
      content: [
        {
          items: [
            '개인정보 열람, 정정, 삭제를 요청할 수 있습니다.',
            '개인정보 처리 정지를 요청할 수 있습니다.',
            '계정 설정에서 직접 정보를 수정하거나 탈퇴할 수 있습니다.',
            '문의 사항은 아래 연락처로 연락해 주세요.',
          ],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-emerald-100 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-gray-600 transition-colors hover:text-emerald-600"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">돌아가기</span>
          </Link>
          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Markly" className="h-7 w-7" />
            <span className="font-semibold text-gray-900">Markly</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <Shield className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">개인정보 처리방침</h1>
          <p className="mx-auto max-w-2xl text-gray-600">
            Markly는 이용자의 개인정보를 중요하게 생각하며, 관련 법령을 준수합니다.
            <br />본 방침은 수집하는 개인정보와 그 이용 방법에 대해 설명합니다.
          </p>
          <p className="mt-4 text-sm text-gray-500">최종 업데이트: {LAST_UPDATED}</p>
        </div>
      </section>

      {/* Content Sections */}
      <section className="px-4 pb-16">
        <div className="mx-auto max-w-4xl space-y-6">
          {sections.map((section, index) => (
            <div key={index} className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                  <section.icon className="h-5 w-5 text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
              </div>
              <div className="space-y-4">
                {section.content.map((contentBlock, blockIndex) => (
                  <div key={blockIndex}>
                    {'subtitle' in contentBlock && contentBlock.subtitle && (
                      <h3 className="mb-2 font-semibold text-gray-700">{contentBlock.subtitle}</h3>
                    )}
                    <ul className="space-y-2">
                      {contentBlock.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start gap-2 text-gray-600">
                          <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Third Party Services */}
          <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                <Database className="h-5 w-5 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">제3자 서비스</h2>
            </div>
            <p className="mb-4 text-gray-600">
              Markly는 서비스 제공을 위해 다음의 제3자 서비스를 이용합니다:
            </p>
            <div className="space-y-3">
              <div className="rounded-lg bg-gray-50 p-4">
                <h3 className="font-semibold text-gray-700">Supabase</h3>
                <p className="mt-1 text-sm text-gray-600">
                  인증 및 데이터베이스 서비스 제공. 이용자의 계정 정보와 북마크 데이터를 안전하게
                  저장합니다.
                </p>
              </div>
              <div className="rounded-lg bg-gray-50 p-4">
                <h3 className="font-semibold text-gray-700">Google Favicon Service</h3>
                <p className="mt-1 text-sm text-gray-600">
                  웹사이트 파비콘 표시를 위해 사용됩니다. 북마크한 웹사이트의 도메인 정보만
                  전송됩니다.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                <Mail className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="mb-2 text-lg font-bold text-gray-900">문의하기</h2>
                <p className="mb-3 text-gray-600">
                  개인정보 처리에 관한 문의사항이 있으시면 언제든지 연락해 주세요.
                </p>
                <a
                  href="mailto:selina202015@gmaiil.com"
                  className="inline-flex items-center gap-2 font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  selina202015@gmaiil.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 px-4 py-8">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <img src="/images/logo.png" alt="Markly" className="h-6 w-6" />
            <span className="font-semibold text-gray-900">Markly</span>
          </div>
          <p className="text-sm text-gray-500">Save what matters.</p>
        </div>
      </footer>
    </div>
  );
}
